<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PublicVolunteerInfo, VolunteerEmailCheckResult } from '@/types/volunteer'
import { checkEmail } from '@/api/publicVolunteer'

const props = defineProps<{
  eventSlug: string
  info: PublicVolunteerInfo
  formData: {
    first_name: string
    last_name: string
    email: string
    phone: string
    emergency_contact_name: string
    emergency_contact_phone: string
  }
  errors: Record<string, string[]>
}>()

const emit = defineEmits<{
  'update:formData': [data: typeof props.formData]
  'next-clicked': []
}>()

const { t } = useI18n()

const showEmergencyContact = ref(false)
const emailCheckResult = ref<VolunteerEmailCheckResult | null>(null)
let emailCheckTimer: ReturnType<typeof setTimeout> | null = null

const requiresAccount = computed(
  () => props.info.edition.volunteer_require_account,
)

const accountRequiredBlocked = computed(() => {
  if (!requiresAccount.value) return false
  if (!emailCheckResult.value) return false
  return !emailCheckResult.value.found
})

const isValid = computed(() => {
  const d = props.formData
  return (
    d.first_name.trim().length > 0 &&
    d.last_name.trim().length > 0 &&
    d.email.trim().length > 0 &&
    d.phone.trim().length > 0 &&
    !accountRequiredBlocked.value
  )
})

function update(field: keyof typeof props.formData, value: string): void {
  emit('update:formData', { ...props.formData, [field]: value })
}

async function onEmailBlur(): Promise<void> {
  const email = props.formData.email.trim()
  if (!email || !email.includes('@')) return
  try {
    emailCheckResult.value = await checkEmail(props.eventSlug, email)
  } catch {
    // Silent failure — never block the wizard on a network error
  }
}

function onEmailInput(): void {
  emailCheckResult.value = null
  if (emailCheckTimer) clearTimeout(emailCheckTimer)
  emailCheckTimer = setTimeout(() => {
    void onEmailBlur()
  }, 600)
}
</script>

<template>
  <div class="space-y-5">
    <h2 class="text-lg font-bold text-slate-900">{{ t('volunteer.wizard.step1.title') }}</h2>

    <!-- First name -->
    <div>
      <label class="block text-sm font-medium text-slate-700 mb-1">
        {{ t('volunteer.wizard.step1.first_name') }} <span class="text-red-500">*</span>
      </label>
      <input
        type="text"
        :value="formData.first_name"
        maxlength="120"
        class="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
        :class="errors['first_name'] ? 'border-red-400' : 'border-slate-300'"
        @input="update('first_name', ($event.target as HTMLInputElement).value)"
      />
      <p v-if="errors['first_name']" class="mt-1 text-xs text-red-600">{{ errors['first_name'][0] }}</p>
    </div>

    <!-- Last name -->
    <div>
      <label class="block text-sm font-medium text-slate-700 mb-1">
        {{ t('volunteer.wizard.step1.last_name') }} <span class="text-red-500">*</span>
      </label>
      <input
        type="text"
        :value="formData.last_name"
        maxlength="120"
        class="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
        :class="errors['last_name'] ? 'border-red-400' : 'border-slate-300'"
        @input="update('last_name', ($event.target as HTMLInputElement).value)"
      />
      <p v-if="errors['last_name']" class="mt-1 text-xs text-red-600">{{ errors['last_name'][0] }}</p>
    </div>

    <!-- Email -->
    <div>
      <label class="block text-sm font-medium text-slate-700 mb-1">
        {{ t('volunteer.wizard.step1.email') }} <span class="text-red-500">*</span>
      </label>
      <input
        type="email"
        :value="formData.email"
        maxlength="255"
        class="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
        :class="errors['email'] ? 'border-red-400' : 'border-slate-300'"
        @input="update('email', ($event.target as HTMLInputElement).value); onEmailInput()"
        @blur="onEmailBlur"
      />
      <p v-if="errors['email']" class="mt-1 text-xs text-red-600">{{ errors['email'][0] }}</p>

      <!-- Email check: account found -->
      <div
        v-if="emailCheckResult?.found"
        class="mt-2 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-sm text-sky-800"
      >
        {{ t('volunteer.wizard.step1.account_found_banner') }}
      </div>

      <!-- Email check: account required but not found -->
      <div
        v-else-if="emailCheckResult && !emailCheckResult.found && requiresAccount"
        class="mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800"
      >
        {{ t('volunteer.wizard.step1.account_required_banner') }}
      </div>

      <!-- Email check: no account but allowed to proceed anonymously -->
      <p
        v-else-if="emailCheckResult && !emailCheckResult.found && !requiresAccount"
        class="mt-2 text-xs text-slate-500"
      >
        {{ t('volunteer.wizard.step1.create_account_link') }}
      </p>
    </div>

    <!-- Phone -->
    <div>
      <label class="block text-sm font-medium text-slate-700 mb-1">
        {{ t('volunteer.wizard.step1.phone') }} <span class="text-red-500">*</span>
      </label>
      <input
        type="tel"
        :value="formData.phone"
        maxlength="40"
        class="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
        :class="errors['phone'] ? 'border-red-400' : 'border-slate-300'"
        @input="update('phone', ($event.target as HTMLInputElement).value)"
      />
      <p v-if="errors['phone']" class="mt-1 text-xs text-red-600">{{ errors['phone'][0] }}</p>
    </div>

    <!-- Emergency contact toggle -->
    <div>
      <button
        type="button"
        class="flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700 font-medium"
        @click="showEmergencyContact = !showEmergencyContact"
      >
        <svg
          class="w-4 h-4 transition-transform"
          :class="showEmergencyContact ? 'rotate-90' : ''"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        {{ t('volunteer.wizard.step1.emergency_contact_toggle') }}
      </button>

      <div v-if="showEmergencyContact" class="mt-3 space-y-3 pl-3 border-l-2 border-sky-100">
        <!-- Emergency contact name -->
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('volunteer.wizard.step1.emergency_contact_name') }}
          </label>
          <input
            type="text"
            :value="formData.emergency_contact_name"
            maxlength="120"
            class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            @input="update('emergency_contact_name', ($event.target as HTMLInputElement).value)"
          />
        </div>
        <!-- Emergency contact phone -->
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('volunteer.wizard.step1.emergency_contact_phone') }}
          </label>
          <input
            type="tel"
            :value="formData.emergency_contact_phone"
            maxlength="40"
            class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            @input="update('emergency_contact_phone', ($event.target as HTMLInputElement).value)"
          />
        </div>
      </div>
    </div>

    <!-- Next button -->
    <div class="pt-2">
      <button
        type="button"
        :disabled="!isValid"
        class="w-full rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        @click="emit('next-clicked')"
      >
        {{ t('common.next') }}
      </button>
    </div>
  </div>
</template>
